import clsx from 'clsx';
import {
  Children,
  forwardRef,
  type ReactNode,
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from 'react';
import { ErrorBoundary } from 'react-error-boundary';
import { useIntl } from 'react-intl';

import * as actionsMenuStyles from './actions-menu/ActionsMenu.sty.js';
import {
  ActionsMenu,
  ActionsMenuInternal,
} from './actions-menu/ActionsMenu.js';
import { getDqlConfig } from './dql/dql-language.js';
import type { DQLEditorValidityInfo } from './dql-editor-types.js';
import { useValidationCallbacks } from './useValidationCallbacks.js';
import { useValidityInfo } from './useValidityInfo.js';
import { ErrorFallback as EditorErrorFallback } from '../../core/components/error-fallback/ErrorFallback.js';
import { useAriaLabelingProps } from '../../core/hooks/useAriaLabelingProps.js';
import { useBehavioralTrackingProps } from '../../core/hooks/useBehavioralTrackingProps.js';
import { useMergeRefs } from '../../core/hooks/useMergeRefs.js';
import { useSafeForwardProps } from '../../core/hooks/useSafeForwardProps.js';
import { createStratoLogger } from '../../core/sdk/sdk-logger.js';
import type { WithChildren } from '../../core/types/with-children.js';
import { isSlot } from '../../core/utils/is-slot.js';
import {
  BaseCodeEditor,
  type BaseCodeEditorRef,
  type SharedBaseCodeEditorProps,
} from '../base-code-editor/BaseCodeEditor.js';
import type { EditorLanguageConfiguration } from '../base-code-editor/configuration/configuration.js';

const COMPONENT_NAME = 'DQLEditor';

/**
 * Props for the `DQLEditor` component.
 * @public
 */
export interface DQLEditorBaseProps
  extends SharedBaseCodeEditorProps,
    WithChildren {
  /**
   * Callback fired when DQL validation starts.
   * This can be used for setting a status that validation is in progress.
   */
  onValidationStart?: () => void;
  /**
   * Callback fired when DQL validation ends.
   * This can be used for resetting a status that validation is in progress and to get the validation result.
   */
  onValidationEnd?: (validityInfo: DQLEditorValidityInfo[]) => void;
  /**
   * Callback fired when new diagnostic information is available.
   * This can be used to get the validation result from outside the editor.
   */
  onValidityChange?: (validityInfo: DQLEditorValidityInfo[]) => void;
}

/**
 * Props for the `DQLEditor` in controlled usage.
 * @public
 */
export type DQLEditorControlledProps = {
  /**
   * The contents of the editor in controlled mode.
   * @defaultValue ''
   */
  value?: string;
  /** Not allowed in controlled mode. */
  defaultValue?: never;
};

/**
 * Props for the `DQLEditor` in uncontrolled usage.
 * @public
 */
export type DQLEditorUncontrolledProps = {
  /** Not allowed in uncontrolled mode. */
  value?: never;
  /** The initial contents of the editor for uncontrolled usage. */
  defaultValue?: string;
  /**
   * `isControlled` has no effect without `value`.
   * @deprecated [APPDEV-17404] BREAKING-CHANGE: Will be removed when controlled behaviour becomes the default.
   */
  isControlled?: never;
};

/**
 * Props for the `DQLEditor` component.
 * @public
 */
export type DQLEditorProps = DQLEditorBaseProps &
  (DQLEditorControlledProps | DQLEditorUncontrolledProps);

/**
 * Ref handle exposed by the `DQLEditor` component.
 * @public
 */
export type DQLEditorRef = {
  /**
   * Moves focus to the editor.
   */
  focus: () => void;
  /**
   * Selects the text within the given character-offset range.
   */
  selectRange: (range: { from: number; to: number }) => void;
  /**
   * The underlying editor container DOM element.
   */
  readonly element: HTMLDivElement | null;
  /**
   * Moves the cursor to the given character offset, or to the end of the document when `'end'` is passed.
   */
  setCursorPosition: (position: number | 'end') => void;
};

/**
 * @internal
 */
const DQLEditorFwd = /* @__PURE__ */ forwardRef<DQLEditorRef, DQLEditorProps>(
  (props, forwardedRef) => {
    const intl = useIntl();

    const defaultPlaceholder = intl.formatMessage({
      defaultMessage: 'Add DQL',
      id: 'Ir0YCuBX6vOM4t0J',
      description: 'Default placeholder for the DQLEditor.',
    });

    const {
      id,
      defaultValue,
      value,
      controlState,
      onChange,
      folding,
      onFoldingChange,
      defaultFolding,
      fullHeight = false,
      lineWrap = false,
      placeholder = defaultPlaceholder,
      readOnly = false,
      spellCheck = false,
      onValidationStart,
      onValidationEnd,
      onValidityChange,
      onFocus,
      onBlur,
      size = 'default',
      required,
      // DEPRECATION: [APPDEV-17404] BREAKING-CHANGE: Remove isControlled prop.
      // eslint-disable-next-line @typescript-eslint/no-deprecated
      isControlled = false,
      children,
      className: consumerClassName,
      style: consumerStyle,
      'data-testid': dataTestId,
      'data-dtrum-mask': dataDtrumMask,
      'data-dtrum-allow': dataDtrumAllow,
      ...remainingProps
    } = props;

    const [behavioralTrackingProps, restProps] =
      useBehavioralTrackingProps(remainingProps);
    const ariaLabelingProps = useAriaLabelingProps(restProps);
    const forwardProps = useSafeForwardProps(restProps, ariaLabelingProps);

    const internalEditorRef = useRef<BaseCodeEditorRef>(null);
    const mergedEditorRef = useMergeRefs<BaseCodeEditorRef>([
      internalEditorRef,
      forwardedRef,
    ]);

    // Tracks the live editor content so the ActionsMenu can copy the current value,
    // not the stale `value` prop (which never updates in uncontrolled usage).
    const internalValueRef = useRef(value ?? defaultValue ?? '');
    useEffect(() => {
      if (value !== undefined) {
        internalValueRef.current = value;
      }
    }, [value]);

    const handleChange = useCallback(
      (newValue: string) => {
        internalValueRef.current = newValue;
        onChange?.(newValue);
      },
      [onChange],
    );

    const getInternalValue = useCallback(() => internalValueRef.current, []);

    const [formatDqlFn, setFormatDqlFn] = useState<
      ((value: string) => string) | undefined
    >(undefined);

    useEffect(() => {
      // `@dynatrace-sdk/dqlint` is an optional peer dependency. The static string
      // lets Vite discover and pre-bundle it in dev mode when the consumer has it
      // installed. `webpackIgnore` prevents webpack from erroring at build time
      // when it is absent; the catch handles the runtime failure in that case.
      import(/* webpackIgnore: true */ '@dynatrace-sdk/dqlint')
        .then(({ formatDql }: { formatDql: (value: string) => string }) => {
          setFormatDqlFn(() => formatDql);
        })
        .catch(() => {
          setFormatDqlFn(undefined);
        });
    }, []);

    const [editorConfig, setEditorConfig] =
      useState<EditorLanguageConfiguration>();

    const [fireValidationStart, fireValidationEnd] = useValidationCallbacks(
      onValidationStart,
      onValidationEnd,
    );
    const setValidityInfo = useValidityInfo(onValidityChange);

    useEffect(() => {
      setEditorConfig(
        getDqlConfig(
          readOnly,
          fireValidationStart,
          fireValidationEnd,
          setValidityInfo,
        ),
      );
    }, [readOnly, setValidityInfo, fireValidationStart, fireValidationEnd]);

    // Detect ActionsMenu slot in children and extract any custom items it carries
    let hasActionsMenu = false;
    let actionsMenuChildren: ReactNode | undefined;
    Children.forEach(children, (child) => {
      if (isSlot(child, ActionsMenu)) {
        hasActionsMenu = true;
        actionsMenuChildren = child.props.children;
      }
    });

    const editorClassName = hasActionsMenu
      ? clsx(
          consumerClassName,
          // Apply right padding on .cm-editor to prevent overlap with the menu trigger
          actionsMenuStyles.editorWithActionsMenu[size],
        )
      : consumerClassName;

    const valueProps = (
      value !== undefined ? { value } : { defaultValue }
    ) satisfies Partial<{ value: string; defaultValue: string }>;

    const handleFormat = () => {
      internalEditorRef.current?.format?.();
    };

    const editor = (
      <BaseCodeEditor
        id={id}
        ref={mergedEditorRef}
        onChange={handleChange}
        placeholder={placeholder}
        controlState={controlState}
        readOnly={readOnly}
        defaultFolding={defaultFolding}
        lineWrap={lineWrap}
        {...valueProps}
        spellCheck={spellCheck}
        fullHeight={fullHeight}
        folding={folding}
        onFoldingChange={onFoldingChange}
        configuration={editorConfig}
        onFocus={onFocus}
        onBlur={onBlur}
        size={size}
        required={required}
        format={formatDqlFn}
        // DEPRECATION: [APPDEV-17404] BREAKING-CHANGE: Remove isControlled prop.
        // eslint-disable-next-line @typescript-eslint/no-deprecated
        isControlled={isControlled}
        className={editorClassName}
        style={consumerStyle}
        data-testid={dataTestId}
        data-dtrum-mask={dataDtrumMask}
        data-dtrum-allow={dataDtrumAllow}
        {...ariaLabelingProps}
        {...behavioralTrackingProps}
        {...forwardProps}
        data-dt-component={COMPONENT_NAME}
      />
    );

    if (hasActionsMenu) {
      return (
        <div
          className={clsx(
            actionsMenuStyles.editorWrapper,
            fullHeight && actionsMenuStyles.editorWrapperFullHeight,
          )}
        >
          {editor}
          <ActionsMenuInternal
            getValue={getInternalValue}
            size={size}
            onFormat={formatDqlFn ? handleFormat : undefined}
          >
            {actionsMenuChildren}
          </ActionsMenuInternal>
        </div>
      );
    }

    return editor;
  },
);

const DQLEditorWithErrorBoundary = /* @__PURE__ */ forwardRef<
  DQLEditorRef,
  DQLEditorProps
>((props, ref) => {
  const stratoLogger = useMemo(
    () => createStratoLogger.bind({ id: props.id })(COMPONENT_NAME),
    [props.id],
  );

  return (
    <ErrorBoundary
      onError={(error) => stratoLogger.error(error?.message, error)}
      FallbackComponent={(fbProps) => <EditorErrorFallback {...fbProps} />}
    >
      <DQLEditorFwd {...props} ref={ref} />
    </ErrorBoundary>
  );
});

/**
 * The `DQLEditor` is specifically designed for editing DQL queries.
 *
 * It further offers syntax highlighting and autocomplete functionality specific to the Dynatrace Query Language (DQL).
 * Once the editor is focused via the keyboard, the user must press "Enter" to start editing
 * and "Escape" to quit editing and return to the keyboard navigation flow.
 * @public
 */
export const DQLEditor = Object.assign(DQLEditorWithErrorBoundary, {
  ActionsMenu,
});
